/**
 * The shared-ride page (owner, 2026-11-04): what a person sees when they open a ride link with no app. A full-height map of
 * Vello's own basemap with the car on it and a sheet with the ride's state; no site navigation, no marketing. An ended, revoked or
 * expired link says so and shows nothing else, and stops reading.
 *
 * Pure decisions live in `../src/trip-page.ts`; the map is `./trip-map-engine.ts`, loaded here only when the page can draw it
 * (WebGL, a position), so MapLibre never reaches any other page. Without WebGL, or before the first position, the state is text on
 * a plain ground. The `engine` and `drawable` props exist for tests (a test DOM has no GPU).
 */
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import wordmarkInk from '@vello/brand/assets/wordmark/svg/vello-wordmark-ink.svg';
import wordmarkWhite from '@vello/brand/assets/wordmark/svg/vello-wordmark-branco.svg';
import { basemapStyleUrl } from '@vello/maps-ui';
import { publicApiOrigin, publicApiUrl, tripSharePageView, TRIP_SHARE_POLL_MS, type PublicRoute, type TripSharePageView } from '../src/public-pages.js';
import { keepsPolling, tripPageModel, tripPageThemeCss, type TripMapScene } from '../src/trip-page.js';
import type { Copy } from './i18n';
import type { MapScheme, TripMapHandle, TripMapOptions } from './trip-map-engine';

export interface TripMapEngine {
  createTripMap(container: HTMLElement, options: TripMapOptions): TripMapHandle;
}

const loadEngine = (): Promise<TripMapEngine> => import('./trip-map-engine');

/** WebGL 2 is there (asked once per page; a test DOM never is). */
let gpu: boolean | undefined;
export function webglAvailable(): boolean {
  if (gpu !== undefined) return gpu;
  if (typeof window === 'undefined' || typeof document === 'undefined' || /jsdom|happydom/i.test(window.navigator.userAgent)) {
    gpu = false;
    return gpu;
  }
  try {
    gpu = document.createElement('canvas').getContext('webgl2') !== null;
  } catch {
    gpu = false;
  }
  return gpu;
}

async function getJson(url: string): Promise<{ status: number; body: unknown }> {
  try {
    const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    return { status: res.status, body };
  } catch {
    return { status: 0, body: undefined };
  }
}

function formatTime(iso: string | undefined, locale: string): string | undefined {
  if (iso === undefined) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  try {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(d);
  } catch {
    return undefined;
  }
}

/** The visitor's colour scheme, followed live. */
function useScheme(): MapScheme {
  const query = (): MediaQueryList | undefined => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : undefined);
  const [scheme, setScheme] = useState<MapScheme>(() => (query()?.matches === true ? 'dark' : 'light'));
  useEffect(() => {
    const mq = query();
    if (mq === undefined) return undefined;
    const on = (): void => setScheme(mq.matches ? 'dark' : 'light');
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return scheme;
}

function Mark(): ReactElement {
  return (
    <picture>
      <source media="(prefers-color-scheme: dark)" srcSet={wordmarkWhite} />
      <img className="trip-mark" src={wordmarkInk} alt="Vello" />
    </picture>
  );
}

export interface TripShareViewProps {
  readonly route: Extract<PublicRoute, { kind: 'trip-share' | 'guest-ride' }>;
  readonly apiOrigin: string | undefined;
  readonly t: Copy;
  readonly locale: string;
  readonly engine?: () => Promise<TripMapEngine>;
  readonly drawable?: () => boolean;
}

export function TripShareView({ route, apiOrigin, t, locale, engine = loadEngine, drawable = webglAvailable }: TripShareViewProps): ReactElement {
  const [view, setView] = useState<TripSharePageView | undefined>(undefined);
  const [mapFailed, setMapFailed] = useState(false);
  const [credit, setCredit] = useState<string | undefined>(undefined);
  const scheme = useScheme();

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async (): Promise<void> => {
      const { status, body } = await getJson(publicApiUrl(apiOrigin, route));
      if (cancelled) return;
      const next = tripSharePageView(status, body);
      setView(next);
      // An ended link is final; a live or unreachable one is read again.
      if (keepsPolling(next)) timer = setTimeout(() => void read(), TRIP_SHARE_POLL_MS);
    };
    void read();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [apiOrigin, route]);

  const canDraw = useMemo(() => drawable(), [drawable]) && !mapFailed;
  const model = tripPageModel(view, canDraw);
  const withMap = model.mode === 'map';

  // ---- the map -------------------------------------------------------------------------------------------------
  const host = useRef<HTMLDivElement | null>(null);
  const sheet = useRef<HTMLElement | null>(null);
  const handle = useRef<TripMapHandle | undefined>(undefined);
  const latest = useRef<{ scene: TripMapScene; scheme: MapScheme }>({ scene: {}, scheme });
  latest.current = { scene: model.mode === 'map' || model.mode === 'plain' ? model.scene : {}, scheme };
  const styleUrl = (s: MapScheme): string => basemapStyleUrl(publicApiOrigin(apiOrigin), s);

  useEffect(() => {
    const el = host.current;
    if (!withMap || el === null) return undefined;
    let cancelled = false;
    void engine()
      .then((loaded) => {
        if (cancelled) return;
        try {
          handle.current = loaded.createTripMap(el, {
            styleUrl: styleUrl(latest.current.scheme),
            scheme: latest.current.scheme,
            scene: latest.current.scene,
            sheetHeightPx: sheet.current?.offsetHeight ?? 0,
            onAttribution: setCredit,
            onFailure: () => setMapFailed(true),
          });
        } catch {
          setMapFailed(true);
        }
      })
      .catch(() => setMapFailed(true));
    return () => {
      cancelled = true;
      handle.current?.remove();
      handle.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withMap, engine, apiOrigin]);

  useEffect(() => {
    if (withMap) handle.current?.update(latest.current.scene, sheet.current?.offsetHeight ?? 0);
  }, [view, withMap]);
  useEffect(() => {
    handle.current?.setScheme(scheme, styleUrl(scheme));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheme]);

  // ---- the page ----------------------------------------------------------------------------------------------------
  const theme = useMemo(() => tripPageThemeCss(), []);
  const body = ((): ReactElement => {
    switch (model.mode) {
      case 'loading':
        return <p className="trip-text">{t('site.trip.loading')}</p>;
      case 'ended':
        return <p className="trip-text" data-testid="trip-ended">{t(model.messageId)}</p>;
      case 'unavailable':
        return <p className="trip-text" data-testid="trip-unavailable">{t('site.trip.unavailable')}</p>;
      default: {
        const updated = formatTime(model.updatedAt, locale);
        return (
          <div data-testid="trip-live">
            <h1 className="trip-state">{t(model.stateMessageId)}</h1>
            {model.etaMinutes !== undefined && <p className="trip-text tnum">{t('site.trip.eta', { minutes: String(model.etaMinutes) })}</p>}
            {model.driverName !== undefined && <p className="trip-text">{t('site.trip.driver', { name: model.driverName })}</p>}
            {model.vehicle !== undefined && <p className="trip-text tnum">{model.vehicle}</p>}
            {model.scene.car === undefined && <p className="trip-text">{t('site.trip.noPosition')}</p>}
            {model.mapUnavailable && <p className="trip-text" data-testid="trip-map-unavailable">{t('site.trip.mapUnavailable')}</p>}
            {updated !== undefined && <p className="trip-note tnum">{t('site.trip.updatedAt', { time: updated })}</p>}
            <p className="trip-note">{t('site.trip.privacy')}</p>
          </div>
        );
      }
    }
  })();

  return (
    <div className="trip-page" data-mode={model.mode} data-scheme={scheme}>
      <style>{theme}</style>
      {withMap && <div ref={host} className="trip-map" role="img" aria-label={t('site.trip.mapLabel')} data-testid="trip-map" />}
      <section ref={sheet} className="trip-sheet" role="status" aria-live="polite">
        <Mark />
        {body}
        {withMap && credit !== undefined && <p className="trip-credit" data-testid="trip-credit">{credit}</p>}
      </section>
    </div>
  );
}
